import { ApiKeyStatus } from '@atlas/types'

/**
 * How a key's status is shown.
 *
 * The status itself is derived server-side and arrives on the DTO — this maps it
 * to a label and a badge variant, and nothing here re-derives it. A closed map
 * rather than a humanized code, because these four are defined by the code (an
 * enum, not administrator-owned data) and each carries a distinct meaning a
 * reader has to act on differently.
 */
interface StatusPresentation {
	label: string
	variant: 'success' | 'muted' | 'danger' | 'warning'
	/** Why the key is in this state, where that is not obvious from the label. */
	hint?: string
}

const PRESENTATION: Record<ApiKeyStatus, StatusPresentation> = {
	[ApiKeyStatus.ACTIVE]: { label: 'Active', variant: 'success' },
	[ApiKeyStatus.EXPIRED]: {
		label: 'Expired',
		variant: 'muted',
		hint: 'Past its expiry date. Create a new key to replace it.',
	},
	[ApiKeyStatus.REVOKED]: {
		label: 'Revoked',
		variant: 'danger',
		hint: 'Revoked by you. This cannot be undone.',
	},
	[ApiKeyStatus.BLOCKED]: {
		label: 'Blocked',
		variant: 'warning',
		hint: 'Unused for 30 days, so it was taken out of service. Create a new key to replace it.',
	},
}

export const apiKeyStatusPresentation = (status: ApiKeyStatus): StatusPresentation => PRESENTATION[status]

/** Whether the key can still be acted on — only an active key is worth revoking. */
export const isApiKeyActionable = (status: ApiKeyStatus): boolean => status === ApiKeyStatus.ACTIVE
